import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Switch,
  Tooltip,
  Tab,
  Tabs,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Delete,
  AddCircleOutline,
  Edit,
  AttachMoney,
  Assignment,
  FolderShared,
} from "@mui/icons-material";
import { product } from "../../../api/paquetes";
import PassengerFileModal from "./PassengerFileModal";
import PassengerDocumentationDialog from './PassengerDocumentationDialog';

export interface Pasajero {
  id?: number;
  nombre: string; // Formato "Apellido, Nombre"
  dni: string;
  pasaporte?: string;
  fecha_nacimiento?: string;
  colegio?: string;
  viaja?: boolean;
  // Nuevos campos
  apellido?: string;
  nombre_solamente?: string;
  direccion?: string;
  ciudad?: string;
  telefono_fijo?: string;
  celular_personal?: string;
  email?: string;
  cuil?: string;
  sexo?: string; // HOMBRE / MUJER
  // Datos del viaje
  emision?: string;
  talle?: string;
  colectivo?: string;
  turno?: string;
  // Documentación
  bolso?: boolean;
  dni_o_pasaporte?: boolean;
  foto_visa?: boolean;
  partida_de_nacimiento?: boolean;
  permiso?: boolean;
  dni_fisico?: boolean;
  fotocopia_de_dni?: boolean;
  ficha_medica?: boolean;
  vacuna?: boolean;
  visa?: boolean;
  pasaporte_europeo?: boolean;
  // Datos del pago
  monto_producto?: number;
  monto_suplementario?: number;
  monto_percepcion?: number;
  porcentaje_liberado?: number;
  nombre_del_pagador?: string;
  tipo_pago?: string;
  categoria_pago?: string;
  pago_confirmado?: boolean; // SI/NO
  cuit_pagador?: string;
  // Datos del contrato
  datos_del_pagador?: string; // Nombre del responsable
  dni_responsable?: string;
  celular_contacto_2?: string;
  dueno_celular_2?: string;
  responsable_apellido?: string;
  responsable_direccion?: string;
  firmo_contrato?: boolean;
  fecha_firma?: string;
  // Observaciones
  observaciones?: string;
  dni_imagen?: string;
  pasaporte_imagen?: string;
  [key: string]: any; // Firma de índice para permitir acceso dinámico
}

export interface GroupPassengersSectionProps {
  pasajeros: Pasajero[];
  nuevoPasajero: Pasajero;
  setNuevoPasajero: (p: Pasajero) => void;
  eliminarPasajero: (pasajero: any) => void;
  paqueteId: number;
  precioVenta: number;
  pagos: any[];
  onAddPassenger: (nuevo: Pasajero) => void;
  onUpdatePassenger: (editado: Pasajero) => void;
  onOpenPagos: (pasajero: any) => void;
  palette?: any;
  tripName?: string;
  tripYear?: string;
  packageType?: string;
}

export const GroupPassengersSection: React.FC<GroupPassengersSectionProps> = ({
  pasajeros = [],
  nuevoPasajero,
  setNuevoPasajero,
  eliminarPasajero,
  paqueteId,
  precioVenta,
  pagos,
  onAddPassenger,
  onUpdatePassenger,
  onOpenPagos,
  palette,
  tripName,
  tripYear,
  packageType,
}) => {
  const [cargando, setCargando] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);
  const [dialogoEliminar, setDialogoEliminar] = useState(false);
  const [pasajeroAEliminar, setPasajeroAEliminar] = useState<any>(null);
  const [dialogoEdicion, setDialogoEdicion] = useState(false);
  const [pasajeroEditado, setPasajeroEditado] = useState<Pasajero | null>(null);
  const [tabEdicion, setTabEdicion] = useState(0);

  const [dialogoFicha, setDialogoFicha] = useState(false);
  const [pasajeroFicha, setPasajeroFicha] = useState<Pasajero | null>(null);
  const [dialogoDoc, setDialogoDoc] = useState(false);
  const [pasajeroDoc, setPasajeroDoc] = useState<Pasajero | null>(null);

  const VIT_PURPLE = palette?.main || "#461264";
  const VIT_GRADIENT = palette?.gradient || "linear-gradient(45deg, #461264 30%, #9c27b0 90%)";

  const validarDNI = (dni: string | number) => {
    const limpio = String(dni).trim();
    if (!limpio) return "El DNI es requerido";
    if (!/^\d{7,10}$/.test(limpio)) return "DNI inválido";
    return null;
  };

  const agregarPasajero = async () => {
    setErrorMensaje(null);
    if (!nuevoPasajero.nombre?.trim()) {
      setErrorMensaje("El nombre es requerido");
      return;
    }
    const errDni = validarDNI(nuevoPasajero.dni);
    if (errDni) {
      setErrorMensaje(errDni);
      return;
    }
    setCargando(true);
    try {
      const pasajeroData = {
        nombre: nuevoPasajero.nombre,
        dni: nuevoPasajero.dni,
        colegio: nuevoPasajero.colegio || "",
        viaja: true,
      };

      const response = await product.createPasajero(paqueteId, pasajeroData);
      const pasajeroCreado = response.data?.data || response.data;
      
      onAddPassenger({
        ...pasajeroCreado,
        viaja: true,
        colegio: nuevoPasajero.colegio,
      });
      setNuevoPasajero({ nombre: "", dni: "", colegio: "" });
    } catch (error: any) {
      setErrorMensaje("Error al agregar pasajero");
    } finally {
      setCargando(false);
    }
  };

  const handleToggleViaja = async (px: any) => {
    const nuevoEstadoViaja = !Boolean(px.viaja);
    const pasajeroActualizado = { ...px, viaja: nuevoEstadoViaja };
    
    onUpdatePassenger(pasajeroActualizado);
    
    try {
      await product.updatePasajero(paqueteId, px.id, { 
        nombre: px.nombre,
        dni: px.dni,
        colegio: px.colegio || "",
        viaja: nuevoEstadoViaja ? 1 : 0,
      });
    } catch (error) {
      console.error("Error al actualizar estado de viaje", error);
      onUpdatePassenger(px);
      setErrorMensaje("No se pudo actualizar el estado de viaje");
    }
  };

  const handleAbrirEdicion = (px: Pasajero) => {
    const parts = (px.nombre || "").split(",");
    const apellido = px.apellido || parts[0]?.trim() || "";
    const nombre_solamente = px.nombre_solamente || parts[1]?.trim() || px.nombre;

    // Split CUIT if exists for the UI
    const cuit = px.cuit_pagador || "";
    const cuit_prefix = cuit.slice(0, 2);
    const cuit_number = cuit.slice(2, 10);
    const cuit_suffix = cuit.slice(10, 11);
    
    setPasajeroEditado({
      ...px,
      apellido,
      nombre_solamente,
      sexo: px.sexo || "MUJER",
      viaja: px.viaja !== undefined ? Boolean(px.viaja) : true,
      bolso: Boolean(px.bolso),
      dni_o_pasaporte: Boolean(px.dni_o_pasaporte),
      foto_visa: Boolean(px.foto_visa),
      partida_de_nacimiento: Boolean(px.partida_de_nacimiento),
      permiso: Boolean(px.permiso),
      dni_fisico: Boolean(px.dni_fisico),
      fotocopia_de_dni: Boolean(px.fotocopia_de_dni),
      ficha_medica: Boolean(px.ficha_medica),
      vacuna: Boolean(px.vacuna),
      visa: Boolean(px.visa),
      pasaporte_europeo: Boolean(px.pasaporte_europeo),
      pago_confirmado: Boolean(px.pago_confirmado),
      monto_producto: px.monto_producto || precioVenta,
      observaciones: px.observaciones || "",
      cuit_prefix,
      cuit_number,
      cuit_suffix,
    });
    setTabEdicion(0);
    setDialogoEdicion(true);
  };

  const handleAbrirFicha = (px: Pasajero) => {
    setPasajeroFicha(px);
    setDialogoFicha(true);
  };

  const handleCambioEditado = (campo: keyof Pasajero, valor: any) => {
    if (pasajeroEditado) {
      setPasajeroEditado({ ...pasajeroEditado, [campo]: valor });
    }
  };

  const handleGuardarEdicion = async () => {
    if (!pasajeroEditado) return;
    setCargando(true);
    try {
      const nombreCompleto = `${pasajeroEditado.apellido || ""}, ${pasajeroEditado.nombre_solamente || ""}`;
      const cuitCompleto = `${pasajeroEditado.cuit_prefix || ""}${pasajeroEditado.cuit_number || ""}${pasajeroEditado.cuit_suffix || ""}`;
      
      const payload = {
        ...pasajeroEditado,
        nombre: nombreCompleto,
        cuit_pagador: cuitCompleto,
        viaja: pasajeroEditado.viaja ? 1 : 0,
        bolso: pasajeroEditado.bolso ? 1 : 0,
        dni_o_pasaporte: pasajeroEditado.dni_o_pasaporte ? 1 : 0,
        foto_visa: pasajeroEditado.foto_visa ? 1 : 0,
        partida_de_nacimiento: pasajeroEditado.partida_de_nacimiento ? 1 : 0,
        permiso: pasajeroEditado.permiso ? 1 : 0,
        dni_fisico: pasajeroEditado.dni_fisico ? 1 : 0,
        fotocopia_de_dni: pasajeroEditado.fotocopia_de_dni ? 1 : 0,
        ficha_medica: pasajeroEditado.ficha_medica ? 1 : 0,
        vacuna: pasajeroEditado.vacuna ? 1 : 0,
        visa: pasajeroEditado.visa ? 1 : 0,
        pasaporte_europeo: pasajeroEditado.pasaporte_europeo ? 1 : 0,
        pago_confirmado: pasajeroEditado.pago_confirmado ? 1 : 0,
        observaciones: pasajeroEditado.observaciones,
      };

      await product.updatePasajero(paqueteId, pasajeroEditado.id!, payload);
      onUpdatePassenger({ ...pasajeroEditado, nombre: nombreCompleto });
      setDialogoEdicion(false);
      setPasajeroEditado(null);
    } catch (error) {
      setErrorMensaje("Error al actualizar");
    } finally {
      setCargando(false);
    }
  };

  const calcularSaldo = (dni: string) => {
    const pagosDelPasajero = (pagos || []).filter((p: any) => 
      p.observacion && 
      p.observacion.includes(`[PASAJERO:${dni}]`) &&
      !p.observacion.toUpperCase().includes("SEÑA")
    );
    
    const totalPagado = pagosDelPasajero.reduce((acc: number, p: any) => acc + (parseFloat(p.importe) || 0), 0);
    return Math.max(0, precioVenta - totalPagado);
  };

  return (
    <Grid item xs={12}>
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #eee" }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: "#444" }}>
          GESTIÓN DE PASAJEROS
        </Typography>

        {errorMensaje && (
          <Typography color="error" variant="caption" sx={{ mb: 2, display: "block" }}>
            {errorMensaje}
          </Typography>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Apellido</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Colegio</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>¿Viaja?</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Saldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pasajeros.map((px: any, idx) => {
                const parts = px.nombre.split(",");
                const apellido = parts[0]?.trim() || "";
                const nombre = parts[1]?.trim() || px.nombre;

                return (
                  <TableRow key={px.id || idx}>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Editar Pasajero">
                          <IconButton size="small" color="primary" onClick={() => handleAbrirEdicion(px)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Pagos">
                          <IconButton size="small" sx={{ color: VIT_PURPLE }} onClick={() => onOpenPagos(px)}>
                            <AttachMoney fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ficha del Pasajero">
                          <IconButton size="small" sx={{ color: "#ff9800" }} onClick={() => handleAbrirFicha(px)}>
                            <Assignment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Documentación">
                          <IconButton size="small" sx={{ color: "#2196f3" }} onClick={() => {
                            setPasajeroDoc(px);
                            setDialogoDoc(true);
                          }}>
                            <FolderShared fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => {
                            setPasajeroAEliminar(px);
                            setDialogoEliminar(true);
                          }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{apellido}</TableCell>
                    <TableCell>{nombre}</TableCell>
                    <TableCell>{px.colegio || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Switch
                          size="small"
                          checked={Boolean(px.viaja)}
                          onChange={() => handleToggleViaja(px)}
                          color="success"
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: Boolean(px.viaja) ? "success.main" : "error.main" }}>
                          {Boolean(px.viaja) ? "SÍ" : "NO"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "primary.main" }}>
                      $ {calcularSaldo(px.dni).toLocaleString()}
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Fila para agregar nuevo */}
              <TableRow sx={{ bgcolor: "#fffbe6" }}>
                <TableCell>
                  <Button
                    startIcon={<AddCircleOutline />}
                    variant="contained"
                    size="small"
                    onClick={agregarPasajero}
                    disabled={cargando}
                    sx={{ textTransform: "none" }}
                  >
                    Agregar
                  </Button>
                </TableCell>
                <TableCell colSpan={2}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Apellido, Nombre"
                    value={nuevoPasajero.nombre}
                    onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, nombre: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Colegio / Institución"
                    value={nuevoPasajero.colegio || ""}
                    onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, colegio: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="DNI"
                    value={nuevoPasajero.dni}
                    onChange={(e) => setNuevoPasajero({ ...nuevoPasajero, dni: e.target.value })}
                  />
                </TableCell>
                <TableCell colSpan={1}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* DIÁLOGO ELIMINAR */}
      <Dialog open={dialogoEliminar} onClose={() => setDialogoEliminar(false)}>
        <DialogTitle>Eliminar Pasajero</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de eliminar a <strong>{pasajeroAEliminar?.nombre}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoEliminar(false)}>Cancelar</Button>
          <Button onClick={() => {
            eliminarPasajero(pasajeroAEliminar);
            setDialogoEliminar(false);
          }} color="error">Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO DE EDICIÓN COMPLETO */}
      <Dialog
        open={dialogoEdicion}
        onClose={() => setDialogoEdicion(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ 
          background: VIT_GRADIENT, 
          color: "white", 
          textAlign: "center",
          py: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            EDITAR PASAJERO: {pasajeroEditado?.apellido}, {pasajeroEditado?.nombre_solamente}
          </Typography>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
          <Tabs value={tabEdicion} onChange={(_, v) => setTabEdicion(v)} variant="fullWidth" sx={{
            "& .MuiTab-root": { fontWeight: 700, fontSize: '0.8rem' },
            "& .Mui-selected": { color: VIT_PURPLE }
          }}>
            <Tab label="Datos del pasajero" />
            <Tab label="Datos del viaje" />
            <Tab label="Datos del pago" />
            <Tab label="Datos del contrato" />
            <Tab label="Observaciones" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {pasajeroEditado && (
            <>
              {/* TAB 1: DATOS DEL PASAJERO */}
              {tabEdicion === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField label="Apellido" fullWidth size="small" value={pasajeroEditado.apellido || ""} onChange={(e) => handleCambioEditado('apellido', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Nombre" fullWidth size="small" value={pasajeroEditado.nombre_solamente || ""} onChange={(e) => handleCambioEditado('nombre_solamente', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="DNI" fullWidth size="small" value={pasajeroEditado.dni || ""} onChange={(e) => handleCambioEditado('dni', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="CUIL" fullWidth size="small" value={pasajeroEditado.cuil || ""} onChange={(e) => handleCambioEditado('cuil', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sexo</InputLabel>
                      <Select value={pasajeroEditado.sexo || ""} label="Sexo" onChange={(e) => handleCambioEditado('sexo', e.target.value)}>
                        <MenuItem value="HOMBRE">HOMBRE</MenuItem>
                        <MenuItem value="MUJER">MUJER</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Fecha de nac." type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={pasajeroEditado.fecha_nacimiento || ""} onChange={(e) => handleCambioEditado('fecha_nacimiento', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Teléfono / Celular" fullWidth size="small" value={pasajeroEditado.celular_personal || ""} onChange={(e) => handleCambioEditado('celular_personal', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Dirección" fullWidth size="small" value={pasajeroEditado.direccion || ""} onChange={(e) => handleCambioEditado('direccion', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Ciudad" fullWidth size="small" value={pasajeroEditado.ciudad || ""} onChange={(e) => handleCambioEditado('ciudad', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Correo electrónico" fullWidth size="small" value={pasajeroEditado.email || ""} onChange={(e) => handleCambioEditado('email', e.target.value)} />
                  </Grid>
                </Grid>
              )}

              {/* TAB 2: DATOS DEL VIAJE */}
              {tabEdicion === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField label="Colegio / Institución" fullWidth size="small" value={pasajeroEditado.colegio || ""} onChange={(e) => handleCambioEditado('colegio', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="EMISIÓN" fullWidth size="small" value={pasajeroEditado.emision || ""} onChange={(e) => handleCambioEditado('emision', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Talle</InputLabel>
                      <Select value={pasajeroEditado.talle || ""} label="Talle" onChange={(e) => handleCambioEditado('talle', e.target.value)}>
                        <MenuItem value="XS">XS</MenuItem>
                        <MenuItem value="S">S</MenuItem>
                        <MenuItem value="M">M</MenuItem>
                        <MenuItem value="L">L</MenuItem>
                        <MenuItem value="XL">XL</MenuItem>
                        <MenuItem value="XXL">XXL</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <TextField label="Colectivo" fullWidth size="small" value={pasajeroEditado.colectivo || ""} onChange={(e) => handleCambioEditado('colectivo', e.target.value)} />
                  </Grid>
                  
                  <Grid item xs={12}><Divider sx={{ my: 1 }}>Documentación / Entrega</Divider></Grid>
                  <Grid item xs={12} md={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {( [
                        { label: "Bolso", field: "bolso" },
                        { label: "DNI o Pasaporte", field: "dni_o_pasaporte" },
                        { label: "Foto Visa", field: "foto_visa" },
                        { label: "Partida de nacimiento", field: "partida_de_nacimiento" },
                        { label: "Permiso", field: "permiso" },
                        { label: "DNI Físico", field: "dni_fisico" },
                        { label: "Fotocopia de DNI", field: "fotocopia_de_dni" },
                        { label: "Ficha médica", field: "ficha_medica" },
                        { label: "Vacuna", field: "vacuna" },
                        { label: "Visa", field: "visa" }
                      ] as { label: string; field: keyof Pasajero }[]).map((item) => (
                        <Button
                          key={item.field}
                          variant={pasajeroEditado[item.field] ? "contained" : "outlined"}
                          size="small"
                          onClick={() => handleCambioEditado(item.field, !pasajeroEditado[item.field])}
                          sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none',
                            bgcolor: pasajeroEditado[item.field] ? "primary.main" : "transparent"
                          }}
                        >
                          {item.label}
                        </Button>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* TAB 3: DATOS DEL PAGO */}
              {tabEdicion === 2 && (
                <Grid container spacing={4}>
                  {/* Columna Izquierda */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[
                        { label: "Monto del producto", field: "monto_producto", type: "number" },
                        { label: "Monto suplementario", field: "monto_suplementario", type: "number" },
                        { label: "Monto percepción", field: "monto_percepcion", type: "number" },
                        { label: "Porcentaje Liberado", field: "porcentaje_liberado", type: "number" }
                      ].map((item) => (
                        <Box key={item.field} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ width: 150, fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                            {item.label}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type={item.type}
                            value={pasajeroEditado[item.field] || 0}
                            onChange={(e) => handleCambioEditado(item.field as keyof Pasajero, item.type === "number" ? parseFloat(e.target.value) : e.target.value)}
                            sx={{ bgcolor: 'white' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* Columna Derecha */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Nombre del Pagador */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          label="NOMBRE DEL PAGADOR"
                          fullWidth
                          size="small"
                          value={pasajeroEditado.nombre_del_pagador || ""}
                          onChange={(e) => handleCambioEditado('nombre_del_pagador', e.target.value)}
                        />
                      </Box>

                      {/* Tipo CUIL/CUIT + Number */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={pasajeroEditado.tipo_pago || ""}
                            onChange={(e) => handleCambioEditado('tipo_pago', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value=""><em>Tipo</em></MenuItem>
                            <MenuItem value="CUIL">CUIL</MenuItem>
                            <MenuItem value="CUIT">CUIT</MenuItem>
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                          <TextField 
                            size="small"
                            sx={{ width: 85 }} 
                            value={pasajeroEditado.cuit_prefix || ""} 
                            onChange={(e) => handleCambioEditado('cuit_prefix', e.target.value.slice(0, 2))}
                            placeholder="__"
                          />
                          <Typography>-</Typography>
                          <TextField 
                            size="small"
                            fullWidth 
                            value={pasajeroEditado.cuit_number || ""} 
                            onChange={(e) => handleCambioEditado('cuit_number', e.target.value.slice(0, 8))}
                            placeholder="________"
                          />
                          <Typography>-</Typography>
                          <TextField 
                            size="small"
                            sx={{ width: 65 }} 
                            value={pasajeroEditado.cuit_suffix || ""} 
                            onChange={(e) => handleCambioEditado('cuit_suffix', e.target.value.slice(0, 1))}
                            placeholder="_"
                          />
                        </Box>
                      </Box>

                      {/* Categoría */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ width: 100, fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                          Categoría
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={pasajeroEditado.categoria_pago || ""}
                            onChange={(e) => handleCambioEditado('categoria_pago', e.target.value)}
                          >
                            <MenuItem value="NO REGISTRADO">NO REGISTRADO</MenuItem>
                            <MenuItem value="RESPONSABLE INSCRIPTO">RESPONSABLE INSCRIPTO</MenuItem>
                            <MenuItem value="MONOTRIBUTISTA">MONOTRIBUTISTA</MenuItem>
                            <MenuItem value="EXCENTO">EXCENTO</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Pagó */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ width: 100, fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                          Pagó
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={pasajeroEditado.pago_confirmado ? "SI" : "NO"}
                            onChange={(e) => handleCambioEditado('pago_confirmado', e.target.value === "SI")}
                          >
                            <MenuItem value="SI">SI</MenuItem>
                            <MenuItem value="NO">NO</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* TAB 4: DATOS DEL CONTRATO */}
              {tabEdicion === 3 && (
                <Grid container spacing={4}>
                  {/* Columna Izquierda */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField label="NOMBRE PADRE/MADRE/TUTOR" fullWidth size="small" value={pasajeroEditado.datos_del_pagador || ""} onChange={(e) => handleCambioEditado('datos_del_pagador', e.target.value)} />
                      <TextField label="APELLIDO PADRE/MADRE/TUTOR" fullWidth size="small" value={pasajeroEditado.responsable_apellido || ""} onChange={(e) => handleCambioEditado('responsable_apellido', e.target.value)} />
                      <TextField label="DNI PADRE/MADRE/TUTOR" fullWidth size="small" value={pasajeroEditado.dni_responsable || ""} onChange={(e) => handleCambioEditado('dni_responsable', e.target.value)} />
                      <TextField label="DIRECCIÓN PADRE/MADRE/TUTOR" fullWidth size="small" value={pasajeroEditado.responsable_direccion || ""} onChange={(e) => handleCambioEditado('responsable_direccion', e.target.value)} />
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Box sx={{ width: '150px', bgcolor: '#f0f0f0', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd' }}>
                          <Typography variant="body2">Firmó</Typography>
                        </Box>
                        <FormControl fullWidth size="small">
                          <Select
                            value={pasajeroEditado.firmo_contrato ? "SI" : "NO"}
                            onChange={(e) => handleCambioEditado('firmo_contrato', e.target.value === "SI")}
                          >
                            <MenuItem value="SI">SI</MenuItem>
                            <MenuItem value="NO">NO</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ width: '150px', bgcolor: '#f0f0f0', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd' }}>
                          <Typography variant="body2">Fecha de firma</Typography>
                        </Box>
                        <TextField 
                          fullWidth 
                          size="small" 
                          type="date" 
                          value={pasajeroEditado.fecha_firma || ""} 
                          onChange={(e) => handleCambioEditado('fecha_firma', e.target.value)} 
                        />
                      </Box>
                    </Box>
                  </Grid>

                  {/* Columna Derecha */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField label="CELULAR 1" fullWidth size="small" value={pasajeroEditado.celular_contacto_1 || ""} onChange={(e) => handleCambioEditado('celular_contacto_1', e.target.value)} />
                      <TextField label="PERTENECIENTE A" fullWidth size="small" value={pasajeroEditado.dueno_celular_1 || ""} onChange={(e) => handleCambioEditado('dueno_celular_1', e.target.value)} />
                      <TextField label="CELULAR 2" fullWidth size="small" value={pasajeroEditado.celular_contacto_2 || ""} onChange={(e) => handleCambioEditado('celular_contacto_2', e.target.value)} />
                      <TextField label="PERTENECIENTE A" fullWidth size="small" value={pasajeroEditado.dueno_celular_2 || ""} onChange={(e) => handleCambioEditado('dueno_celular_2', e.target.value)} />
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* TAB 5: OBSERVACIONES */}
              {tabEdicion === 4 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>Nueva Observación</Typography>
                    <TextField multiline rows={6} fullWidth size="small" value={pasajeroEditado.observaciones || ""} onChange={(e) => handleCambioEditado('observaciones', e.target.value)} placeholder="Agregue comentarios relevantes aquí..." />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 0 }}>
          <Button 
            onClick={() => setDialogoEdicion(false)} 
            sx={{ 
              flex: 1, 
              py: 2, 
              borderRadius: 0, 
              bgcolor: "#d32f2f", 
              color: "white",
              "&:hover": { bgcolor: "#b71c1c" }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleGuardarEdicion} 
            disabled={cargando}
            sx={{ 
              flex: 1, 
              py: 2, 
              borderRadius: 0, 
              bgcolor: "#4caf50", 
              color: "white",
              "&:hover": { bgcolor: "#388e3c" }
            }}
          >
            {cargando ? <CircularProgress size={24} color="inherit" /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO FICHA DEL PASAJERO */}
      <PassengerFileModal
        open={dialogoFicha}
        onClose={() => setDialogoFicha(false)}
        pasajero={pasajeroFicha}
        onDelete={(px) => {
          setPasajeroAEliminar(px);
          setDialogoEliminar(true);
          setDialogoFicha(false);
        }}
        currentTrip={tripName}
        currentYear={tripYear}
        packageType={packageType}
        // onPrintFicha/Contratos can be implemented later if needed
      />

      <PassengerDocumentationDialog
        open={dialogoDoc}
        onClose={() => setDialogoDoc(false)}
        pasajero={pasajeroDoc}
        onSave={(id, dniImg, pasaporteImg) => {
          if (pasajeroDoc) {
            onUpdatePassenger({
              ...pasajeroDoc,
              dni_imagen: dniImg || undefined,
              pasaporte_imagen: pasaporteImg || undefined
            });
          }
        }}
      />

    </Grid>
  );
};
